import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCheckCircle, FaLock, FaMobileAlt, FaRegUser } from 'react-icons/fa';
import DashboardLayout from '../components/DashboardLayout';
import { apiRequest } from '../utils/api';
import { formatEmployeeId } from '../utils/employeeId';
import { getActiveCompanyId } from '../utils/companyContext';
import './EmployeeLoginCreate.css';

const MIN_PASSWORD_LENGTH = 4;

const formatUsernameSuggestion = (suggestion) => {
  if (!suggestion) {
    return '';
  }

  const normalized = suggestion.trim();
  if (!normalized) {
    return '';
  }

  return normalized.replace(/^EMP/i, 'GSM').toUpperCase();
};

const sanitizeEmployeeDetails = (employee, isNewlyCreated) => {
  if (!employee) {
    return null;
  }

  if (!isNewlyCreated) {
    return { ...employee };
  }

  const cleanedEmployee = { ...employee };
  cleanedEmployee.email = '';
  cleanedEmployee.outlet = null;
  cleanedEmployee.outletId = null;

  return cleanedEmployee;
};

const normalizeMobileInput = (value) => {
  const digits = value ? String(value).replace(/\D/g, '') : '';
  if (!digits) {
    return '';
  }

  if (digits.length >= 10) {
    return digits.slice(-10);
  }

  return digits;
};

const EmployeeLoginCreate = () => {
  const navigate = useNavigate();
  const [mobileInput, setMobileInput] = useState('');
  const [stage, setStage] = useState('lookup');
  const [lookupDetails, setLookupDetails] = useState(null);
  const [employeeName, setEmployeeName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [success, setSuccess] = useState('');
  const [createdLogin, setCreatedLogin] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [outletError, setOutletError] = useState('');
  const [outletLoading, setOutletLoading] = useState(false);
  const [selectedOutletId, setSelectedOutletId] = useState('');
  const [educationMandatory, setEducationMandatory] = useState(true);
  const [activeCompanyId, setActiveCompanyId] = useState(() => getActiveCompanyId());

  const normalizedMobile = useMemo(() => {
    if (lookupDetails?.mobile) {
      return lookupDetails.mobile;
    }
    return normalizeMobileInput(mobileInput);
  }, [lookupDetails?.mobile, mobileInput]);

  const sortedOutlets = useMemo(() => {
    return [...outlets].sort((first, second) => {
      const firstName = (first.branchName || first.name || '').toLowerCase();
      const secondName = (second.branchName || second.name || '').toLowerCase();

      if (firstName < secondName) {
        return -1;
      }
      if (firstName > secondName) {
        return 1;
      }
      return 0;
    });
  }, [outlets]);

  useEffect(() => {
    let isMounted = true;

    const fetchOutlets = async () => {
      setOutletLoading(true);
      setOutletError('');

      try {
        if (!activeCompanyId) {
          setOutlets([]);
          return;
        }

        const params = new URLSearchParams({ companyId: String(activeCompanyId) });
        const response = await apiRequest(`/api/admin/settings/outlets?${params.toString()}`, {}, navigate);
        if (isMounted) {
          setOutlets(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        if (isMounted) {
          setOutletError(err.message || 'Unable to load outlets.');
        }
      } finally {
        if (isMounted) {
          setOutletLoading(false);
        }
      }
    };

    fetchOutlets();

    return () => {
      isMounted = false;
    };
  }, [activeCompanyId, navigate]);

  useEffect(() => {
    if (stage === 'login') {
      const outletId = lookupDetails?.employee?.outlet?.id;
      setSelectedOutletId(outletId ? String(outletId) : '');
    }
  }, [lookupDetails?.employee?.outlet?.id, stage]);

  useEffect(() => {
    const handleActiveCompanyChanged = (event) => {
      const nextCompanyId = event?.detail?.id ?? getActiveCompanyId();
      setActiveCompanyId(nextCompanyId);
      resetForm();
    };

    window.addEventListener('activeCompanyChanged', handleActiveCompanyChanged);

    return () => {
      window.removeEventListener('activeCompanyChanged', handleActiveCompanyChanged);
    };
  }, []);

  const resetForm = () => {
    setStage('lookup');
    setLookupDetails(null);
    setMobileInput('');
    setEmployeeName('');
    setUsername('');
    setPassword('');
    setError('');
    setInfo('');
    setSuccess('');
    setCreatedLogin(null);
    setSelectedOutletId('');
    setEducationMandatory(true);
  };

  const handleLookup = async (event) => {
    event.preventDefault();

    const trimmedMobile = mobileInput.trim();
    const normalized = normalizeMobileInput(trimmedMobile);
    if (!normalized || normalized.length < 10) {
      setError('Enter a valid 10-digit mobile number to continue.');
      return;
    }

    if (!activeCompanyId) {
      setError('Select a company before creating employee logins.');
      return;
    }

    setLoading(true);
    setError('');
    setInfo('');
    setSuccess('');
    setCreatedLogin(null);

    try {
      const response = await apiRequest(
        '/api/admin/employee-logins/lookup',
        {
          method: 'POST',
          body: { mobile: trimmedMobile, companyId: activeCompanyId },
        },
        navigate,
      );

      const data = response.data ?? {};
      const normalizedMobileValue = data.normalizedMobile || normalized;

      setLookupDetails({
        mobile: normalizedMobileValue,
        employee: data.matchedEmployee ?? null,
        existingLogin: data.existingLogin ?? null,
        suggestion: formatUsernameSuggestion(data.suggestions?.username),
        isNewEmployee: false,
      });

      setEmployeeName(data.matchedEmployee?.name ?? '');
      setUsername(formatUsernameSuggestion(data.suggestions?.username));
      setPassword('');
      setInfo(response.message || '');

      if (data.matchedEmployee) {
        if (data.existingLogin) {
          setStage('lookup');
          setInfo(
            response.message ||
              'This employee already has login credentials configured. Use the edit option from the employee profile to update the login.',
          );
        } else {
          setStage('login');
        }
      } else {
        setStage('newEmployee');
        setEmployeeName('');
        setInfo(
          response.message ||
            'No employee mapping found for this mobile number. Provide the basic details to create one.',
        );
      }
    } catch (err) {
      setError(err.message || 'Failed to check the mobile number.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewEmployeeSubmit = (event) => {
    event.preventDefault();

    const trimmedName = employeeName.trim();
    if (!trimmedName) {
      setError('Employee name is required.');
      return;
    }

    setError('');
    setLookupDetails((previous) => ({
      mobile: previous?.mobile ?? normalizedMobile,
      employee: {
        id: null,
        name: trimmedName,
        phone: previous?.mobile ?? normalizedMobile,
      },
      existingLogin: null,
      suggestion: previous?.suggestion ?? '',
      isNewEmployee: true,
    }));

    if (!username) {
      const fallbackSuggestion =
        formatUsernameSuggestion(lookupDetails?.suggestion) ||
        (normalizedMobile ? `GSM${normalizedMobile.slice(-4)}` : '');
      if (fallbackSuggestion) {
        setUsername(fallbackSuggestion.toUpperCase());
      }
    }

    setInfo('Enter the username and password to finish creating the login credentials.');
    setStage('login');
  };

  const handleLoginCreation = async (event) => {
    event.preventDefault();

    const currentMobile = lookupDetails?.mobile ?? normalizedMobile;
    if (!currentMobile) {
      setError('We could not determine the mobile number. Please start again.');
      setStage('lookup');
      return;
    }

    if (!activeCompanyId) {
      setError('Select a company before creating employee logins.');
      return;
    }

    const trimmedUsername = username.trim();
    if (!trimmedUsername) {
      setError('Username is required.');
      return;
    }

    const trimmedPassword = password.trim();
    if (trimmedPassword.length < MIN_PASSWORD_LENGTH) {
      setError('Password must be at least 4 characters long.');
      return;
    }

    const payload = {
      mobile: currentMobile,
      username: trimmedUsername,
      password: trimmedPassword,
      companyId: activeCompanyId,
      educationMandatory,
    };

    const employeeLabel = lookupDetails?.employee?.name || employeeName.trim();
    if (lookupDetails?.isNewEmployee && employeeLabel) {
      payload.employeeName = employeeLabel;
    }

    if (selectedOutletId) {
      const outletId = Number(selectedOutletId);
      payload.outletId = Number.isNaN(outletId) ? null : outletId;
    } else {
      payload.outletId = null;
    }

    setLoading(true);
    setError('');
    setInfo('');
    setSuccess('');
    setCreatedLogin(null);

    try {
      const response = await apiRequest(
        '/api/admin/employee-logins/setup',
        {
          method: 'POST',
          body: payload,
        },
        navigate,
      );

      const responseData = response.data ?? {};

      const sanitizedEmployee = sanitizeEmployeeDetails(
        responseData.employee ?? lookupDetails?.employee ?? null,
        lookupDetails?.isNewEmployee,
      );

      if (sanitizedEmployee && selectedOutletId) {
        const selectedOutlet = outlets.find((outlet) => String(outlet.id) === selectedOutletId);
        if (selectedOutlet) {
          sanitizedEmployee.outlet = {
            id: selectedOutlet.id,
            name: selectedOutlet.branchName || selectedOutlet.name || '',
          };
        }
      }

      setLookupDetails({
        mobile: responseData.mobile ?? currentMobile,
        employee: sanitizedEmployee,
        existingLogin: responseData.login ?? null,
        suggestion: formatUsernameSuggestion(responseData.login?.username),
        isNewEmployee: false,
      });

      setCreatedLogin(responseData.login ?? null);
      setPassword('');
      setSuccess(response.message || 'Mobile login created successfully.');
      setStage('result');
    } catch (err) {
      setError(err.message || 'Failed to create mobile login credentials.');
    } finally {
      setLoading(false);
    }
  };

  const renderSummary = () => {
    if (!lookupDetails?.employee) {
      return null;
    }

    const resolvedOutlet = (() => {
      if (selectedOutletId) {
        const selectedOutlet = outlets.find((outlet) => String(outlet.id) === selectedOutletId);
        if (selectedOutlet) {
          return selectedOutlet.branchName || selectedOutlet.name;
        }
      }
      return lookupDetails.employee.outlet?.name || lookupDetails.employee.outlet?.branchName || '';
    })();

    const summary = [
      { label: 'Employee Name', value: lookupDetails.employee.name || '—' },
      { label: 'Mobile Number', value: lookupDetails.mobile ? `+91 ${lookupDetails.mobile}` : '—' },
      { label: 'Show ID', value: formatEmployeeId(lookupDetails.employee.showId) || '—' },
      { label: 'Permanent ID', value: lookupDetails.employee.permanentId || '—' },
      {
        label: 'Outlet',
        value: resolvedOutlet || '—',
      },
      {
        label: 'Education mandatory',
        value: educationMandatory ? 'Yes' : 'No (allowed to skip)',
      },
    ];

    return (
      <dl className="employee-login-create__summary-list">
        {summary.map((item) => (
          <div key={item.label}>
            <dt>{item.label}</dt>
            <dd>{item.value}</dd>
          </div>
        ))}
      </dl>
    );
  };

  const hasBasicDetailsForCredentials = Boolean(
    normalizedMobile && (lookupDetails?.employee?.name || employeeName.trim()),
  );
  const canShowCredentialFields = hasBasicDetailsForCredentials;

  return (
    <DashboardLayout title="Create Employee Mobile Login">
      <div className="employee-login-create">
        <header className="employee-login-create__header">
          <button
            type="button"
            className="employee-login-create__back"
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft aria-hidden="true" />
            <span>Back</span>
          </button>
          <div className="employee-login-create__intro">
            <h1>Create Mobile Login</h1>
            <p>Generate credentials for employees to access the HRMS mobile application.</p>
          </div>
        </header>

        {(error || info || success) && (
          <div className="employee-login-create__messages">
            {error && (
              <div className="employee-login-create__alert employee-login-create__alert--error" role="alert">
                {error}
              </div>
            )}
            {info && !success && (
              <div className="employee-login-create__alert employee-login-create__alert--info" role="status">
                {info}
              </div>
            )}
            {success && (
              <div className="employee-login-create__alert employee-login-create__alert--success" role="status">
                {success}
              </div>
            )}
          </div>
        )}

        {stage === 'lookup' && (
          <section className="employee-login-create__card">
            <h2>1. Search by mobile number</h2>
            <form onSubmit={handleLookup} className="employee-login-create__form">
              <label className="employee-login-create__field">
                <span className="employee-login-create__label">
                  <FaMobileAlt aria-hidden="true" /> Mobile number
                </span>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={mobileInput}
                  onChange={(event) => setMobileInput(event.target.value)}
                  placeholder="Enter employee mobile number"
                  autoFocus
                />
              </label>
              <div className="employee-login-create__actions">
                <button type="submit" className="employee-login-create__primary" disabled={loading}>
                  {loading ? 'Checking…' : 'Continue'}
                </button>
                <Link to="/dashboard/employee-logins" className="employee-login-create__link">
                  View all logins
                </Link>
              </div>
            </form>
          </section>
        )}

        {stage === 'newEmployee' && (
          <section className="employee-login-create__card">
            <h2>2. Provide basic employee details</h2>
            <form onSubmit={handleNewEmployeeSubmit} className="employee-login-create__form">
              <div className="employee-login-create__grid">
                <label className="employee-login-create__field">
                  <span className="employee-login-create__label">
                    <FaMobileAlt aria-hidden="true" /> Mobile number
                  </span>
                  <input type="text" value={`+91 ${normalizedMobile}`} readOnly />
                </label>
                <label className="employee-login-create__field">
                  <span className="employee-login-create__label">
                    <FaRegUser aria-hidden="true" /> Employee name
                  </span>
                  <input
                    type="text"
                    value={employeeName}
                    onChange={(event) => setEmployeeName(event.target.value)}
                    placeholder="Enter employee full name"
                  />
                </label>
              </div>
              <div className="employee-login-create__actions">
                <button type="submit" className="employee-login-create__primary" disabled={loading}>
                  {loading ? 'Processing…' : 'Next'}
                </button>
                <button type="button" className="employee-login-create__link" onClick={resetForm}>
                  Start over
                </button>
              </div>
            </form>
          </section>
        )}

        {stage === 'login' && (
          <section className="employee-login-create__card">
            <h2>{lookupDetails?.isNewEmployee ? '3. Create login credentials' : '2. Create login credentials'}</h2>
            <div className="employee-login-create__summary">{renderSummary()}</div>
            <form onSubmit={handleLoginCreation} className="employee-login-create__form">
              <label className="employee-login-create__field">
                <span className="employee-login-create__label">Outlet (optional)</span>
                <select
                  value={selectedOutletId}
                  onChange={(event) => {
                    setSelectedOutletId(event.target.value);
                  }}
                  disabled={outletLoading}
                >
                  <option value="">No outlet selected</option>
                  {sortedOutlets.map((outlet) => (
                    <option key={outlet.id} value={outlet.id}>
                      {outlet.branchName || outlet.name || outlet.showId || `Outlet #${outlet.id}`}
                    </option>
                  ))}
                </select>
                {outletLoading && <span className="employee-login-create__helper">Loading outlets…</span>}
                {outletError && (
                  <span className="employee-login-create__helper employee-login-create__helper--error">{outletError}</span>
                )}
              </label>
              <fieldset className="employee-login-create__field employee-login-create__toggle">
                <legend className="employee-login-create__label">Education mandatory?</legend>
                <div className="employee-login-create__toggle-options" role="radiogroup" aria-label="Education validation">
                  <label className="employee-login-create__toggle-option">
                    <input
                      type="radio"
                      name="educationMandatory"
                      value="yes"
                      checked={educationMandatory === true}
                      onChange={() => setEducationMandatory(true)}
                    />
                    <span>Yes, collect education details</span>
                  </label>
                  <label className="employee-login-create__toggle-option">
                    <input
                      type="radio"
                      name="educationMandatory"
                      value="no"
                      checked={educationMandatory === false}
                      onChange={() => setEducationMandatory(false)}
                    />
                    <span>No, allow registration without education</span>
                  </label>
                </div>
              </fieldset>
              {canShowCredentialFields && (
                <>
                  <label className="employee-login-create__field">
                    <span className="employee-login-create__label">
                      <FaRegUser aria-hidden="true" /> Username
                    </span>
                    <input
                      type="text"
                      value={username}
                      onChange={(event) => setUsername(event.target.value)}
                      placeholder="Choose a unique username"
                      autoComplete="username"
                    />
                  </label>
                  <label className="employee-login-create__field">
                    <span className="employee-login-create__label">
                      <FaLock aria-hidden="true" /> Password
                    </span>
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder={`Enter a password (${MIN_PASSWORD_LENGTH}+ characters)`}
                      autoComplete="new-password"
                    />
                  </label>
                </>
              )}
              <div className="employee-login-create__actions">
                <button type="submit" className="employee-login-create__primary" disabled={loading}>
                  {loading ? 'Saving…' : 'Create login'}
                </button>
                <button type="button" className="employee-login-create__link" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </section>
        )}

        {stage === 'result' && (
          <section className="employee-login-create__card">
            <h2 className="employee-login-create__result-heading">
              <FaCheckCircle aria-hidden="true" /> Login ready
            </h2>
            <div className="employee-login-create__summary">{renderSummary()}</div>
            {createdLogin && (
              <div className="employee-login-create__credentials" aria-live="polite">
                <p>
                  Share the credentials below with the employee. Encourage them to change the password after the first login.
                </p>
                <div>
                  <span>Username</span>
                  <code>{createdLogin.username}</code>
                </div>
                <div>
                  <span>Temporary password</span>
                  <code>{createdLogin.temporaryPassword || '—'}</code>
                </div>
              </div>
            )}
            <div className="employee-login-create__actions">
              <button type="button" className="employee-login-create__primary" onClick={resetForm}>
                Create another login
              </button>
              <Link to="/dashboard/employee-logins" className="employee-login-create__link">
                Go to login list
              </Link>
            </div>
          </section>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EmployeeLoginCreate;
